import Immutable from "immutable";
import React from "react";
import {
  Change,
  hasChanges,
  MessageTemplateId,
  MessageTemplateIdType,
} from "src/scenes/orgAdmin/formTemplates/types/common";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/editMessagesDraft";
import * as State from "src/scenes/orgAdmin/formTemplates/types/editMessagesState";
import * as GQL from "src/types/graphql";

export type EditMessagesInitialValues = {
  editMessages: Draft.AutomaticMessageFormType;
};

export type Action =
  | {
      type: "UpdateEditMessages";
      editMessages: Draft.EditMessagesDraft;
      messageTemplateType: GQL.message_template_type_enum;
    }
  | {
      type: "Refresh";
      initialValues: EditMessagesInitialValues;
      messageTemplateType: GQL.message_template_type_enum;
    }
  | { type: "Reset"; messageTemplateType: GQL.message_template_type_enum }
  | { type: "ResetComplete" };

export function reducer(state: State.State, action: Action): State.State {
  switch (action.type) {
    case "Reset":
      return reset(state, action.messageTemplateType);

    case "ResetComplete":
      return state.set("reset", undefined);

    case "UpdateEditMessages":
      return updateEditMessages(
        state,
        action.editMessages,
        action.messageTemplateType
      );

    case "Refresh":
      return refresh(action.initialValues, action.messageTemplateType);

    default:
      const _exhaustiveCheck: never = action;
      return _exhaustiveCheck;
  }
}

function reset(
  state: State.State,
  messageTemplateType: GQL.message_template_type_enum
): State.State {
  const editMessages = state.get("editMessages").map(clearChange);

  return state
    .set("editMessages", editMessages)
    .set("reset", messageTemplateType);
}

function clearChange<T, U>(change: Change<T, U>): Change<T, U> {
  return { original: change.original, draft: undefined };
}

function updateEditMessages(
  state: State.State,
  draft: Draft.EditMessagesDraft,
  messageTemplateType: GQL.message_template_type_enum
): State.State {
  const originalEditMessagesState = state
    .get("editMessages")
    .get(MessageTemplateId(messageTemplateType));
  if (!originalEditMessagesState) {
    return state;
  }

  const original = originalEditMessagesState.original;
  const hasChangesResult = hasChanges(original, draft);

  const updatedState: State.State = state.update(
    "editMessages",
    (editMessages) =>
      editMessages.set(MessageTemplateId(messageTemplateType), {
        ...originalEditMessagesState,
        draft: hasChangesResult ? draft : undefined,
      })
  );

  return updatedState;
}

function toState(
  initialValues: EditMessagesInitialValues,
  messageTemplateType: GQL.message_template_type_enum
): State.State {
  const editMessages: [MessageTemplateIdType, State.EditMessagesChange][] = [
    [
      MessageTemplateId(messageTemplateType),
      { original: initialValues.editMessages, draft: undefined },
    ],
  ];

  return State.NewState({
    editMessages: Immutable.Map(editMessages),
  });
}

function refresh(
  initialValues: EditMessagesInitialValues,
  messageTemplateType: GQL.message_template_type_enum
): State.State {
  return reset(
    toState(initialValues, messageTemplateType),
    messageTemplateType
  );
}

export function useFormTemplateEditMessagesProcessor(
  initialValues: EditMessagesInitialValues,
  messageTemplateType: GQL.message_template_type_enum
) {
  const initialState = toState(initialValues, messageTemplateType);
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return { state, dispatch };
}
